import React from 'react';
import { Request } from '../services/requestsService';
import dataService from '../services/dataService';
import authService from '../services/authService';
import './TaskDetailModal.css';

interface TaskDetailModalProps {
  isOpen: boolean;
  task: Request | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ isOpen, task, onClose, onSuccess }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showAcceptOptions, setShowAcceptOptions] = React.useState(false);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return '#ef4444';
      case 'MEDIUM':
        return '#f59e0b';
      case 'LOW':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'Высокий';
      case 'MEDIUM':
        return 'Средний';
      case 'LOW':
        return 'Низкий';
      default:
        return priority;
    }
  };

  const getStatusColor = (status: any) => {
    return status?.color || '#6b7280';
  };

  const handleTakeInWork = async () => {
    if (!task) return;

    try {
      setLoading(true);
      setError(null);
      
      const userId = authService.getUserId();
      if (!userId) {
        throw new Error('Не удалось определить пользователя');
      }
      
      console.log('Taking task in work:', task.id, 'for user:', userId);
      await dataService.takeRequestInWork(task.id, userId);
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error taking task in work:', err);
      setError('Ошибка при принятии задачи в работу');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTask = () => {
    setShowAcceptOptions(true);
  };

  const handleCancelAccept = () => {
    setShowAcceptOptions(false);
  };

  if (!isOpen || !task) {
    return null;
  }

  return (
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="task-modal-header">
          <h2>Детали задачи</h2>
          <button className="task-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="task-modal-body">
          <div className="task-modal-main-info">
            <div className="task-modal-title-section">
              <h3 className="task-modal-title">{task.title}</h3>
              <div className="task-modal-badges">
                <span 
                  className="task-modal-priority-badge"
                  style={{ backgroundColor: getPriorityColor(task.priority) }}
                >
                  {getPriorityText(task.priority)}
                </span>
                <span 
                  className="task-modal-status-badge"
                  style={{ backgroundColor: getStatusColor(task.status) }}
                >
                  {task.status?.name}
                </span>
              </div>
            </div>

            <div className="task-modal-description">
              <h4>Описание</h4>
              <p>{task.description}</p>
            </div>
          </div>

          <div className="task-modal-details">
            <div className="task-modal-section">
              <h4>Информация о рейсе</h4>
              <div className="task-modal-detail-grid">
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Номер рейса:</span>
                  <span className="task-modal-detail-value">{task.flight?.flightNumber}</span>
                </div>
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Маршрут:</span>
                  <span className="task-modal-detail-value">
                    {task.flight?.departureAirport} → {task.flight?.arrivalAirport}
                  </span>
                </div>
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Время вылета:</span>
                  <span className="task-modal-detail-value">
                    {task.flight?.departureTime ? formatDateTime(task.flight.departureTime) : 'Не указано'}
                  </span>
                </div>
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Время прилета:</span>
                  <span className="task-modal-detail-value">
                    {task.flight?.arrivalTime ? formatDateTime(task.flight.arrivalTime) : 'Не указано'}
                  </span>
                </div>
              </div>
            </div>

            <div className="task-modal-section">
              <h4>Информация о машине</h4>
              <div className="task-modal-detail-grid">
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Модель:</span>
                  <span className="task-modal-detail-value">{task.car?.name} {task.car?.model}</span>
                </div>
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Номер:</span>
                  <span className="task-modal-detail-value">{task.car?.number}</span>
                </div>
              </div>
            </div>

            <div className="task-modal-section">
              <h4>Информация о задаче</h4>
              <div className="task-modal-detail-grid">
                {task.aircraftParts && task.aircraftParts.length > 0 ? (
                  <div className="task-modal-detail-item">
                    <span className="task-modal-detail-label">Части самолета:</span>
                    <div className="task-modal-detail-value">
                      <ul className="aircraft-parts-list">
                        {task.aircraftParts.map((part, index) => (
                          <li key={index}>
                            {part.aircraftPartName}
                            {part.description && <span className="part-description"> - {part.description}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="task-modal-detail-item">
                    <span className="task-modal-detail-label">Части самолета:</span>
                    <span className="task-modal-detail-value">Не указаны</span>
                  </div>
                )}
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Создано:</span>
                  <span className="task-modal-detail-value">{formatDateTime(task.createdAt)}</span>
                </div>
                <div className="task-modal-detail-item">
                  <span className="task-modal-detail-label">Создатель:</span>
                  <span className="task-modal-detail-value">
                    {task.creator?.name} {task.creator?.surname}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="task-modal-error">
              <p>{error}</p>
            </div>
          )}

          {showAcceptOptions && (
            <div className="task-modal-info">
              <div className="task-modal-info-icon">⚠️</div>
              <div className="task-modal-info-content">
                <h4>Подтверждение принятия заявки</h4>
                <p>Вы собираетесь принять заявку "{task.title}" в работу. После принятия статус заявки изменится на "В работе".</p>
              </div>
            </div>
          )}
        </div>

        <div className="task-modal-footer">
          <button className="task-modal-button secondary" onClick={onClose}>
            Закрыть
          </button>
          {task.status?.name === 'Новая' && !showAcceptOptions && (
            <button 
              className="task-modal-button primary"
              onClick={handleAcceptTask}
            >
              Принять заявку
            </button>
          )}
          {task.status?.name === 'Новая' && showAcceptOptions && (
            <>
              <button 
                className="task-modal-button secondary"
                onClick={handleCancelAccept}
              >
                Отмена
              </button>
              <button 
                className="task-modal-button primary"
                onClick={handleTakeInWork}
                disabled={loading}
              >
                {loading ? 'Принимаю...' : 'Подтвердить принятие'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal; 